import { useEffect, useRef } from 'react'
import { useAccountStore } from '@/stores/account-store'
import { storageScopeId } from '@/services/auth/types'
import { edgeUsersApi, useUpdateUserMutation } from '@/services/api/users-api'
import {
  onboardingSnapshot,
  useOnboardingStore,
  validateOnboardingState,
  type OnboardingState,
} from './onboarding-store'

/**
 * Whether a server record carries anything worth adopting. An empty object
 * must never wipe a fresher local state, but a pre-onboarding user (had
 * layouts, never saw the interview) has no startedAt and can still have
 * activation, tips or a checklist on record; dropping those re-fires
 * user_activated and the next push overwrites the server copy.
 */
function hasProgress(s: OnboardingState): boolean {
  return (
    s.startedAt != null ||
    Object.keys(s.activation.behaviors).length > 0 ||
    s.activation.activatedAt != null ||
    s.tipsSeen.length > 0 ||
    s.checklist != null
  )
}

/**
 * Two-way sync of onboarding state with the user's server profile
 * (`onboarding` key), so interview answers / persona / checklist follow the
 * user across devices. SERVER is the source of truth; localStorage (in
 * onboarding-store) is only the fast local cache. Mirrors
 * useLearnProgressServerSync, with a storage scope and a hydration gate:
 *
 * - Storage scope: points the store's cache at the signed-in user
 *   (storageScopeId, like the IDB workspace cache) so a second account on
 *   the same browser never inherits the first one's state. Unmount resets
 *   it: logout tears down the authenticated tree before the session clears.
 * - Hydration gate: the store's `hydrated` flag. On the first profile load
 *   for a scope in this page lifetime, apply the server value via
 *   `hydrate()` (server wins on login). A user with no saved progress, or a
 *   failed profile request, is hydrated-empty: keep local and trigger NO
 *   write (`lastSynced` seeds to local). After a failed request that is
 *   provisional: nothing pushes, and a profile that loads later is still
 *   applied unless local progress was made meanwhile. A profile that is not
 *   the signed-in account's (cached past logout) is held as still loading. Re-entering a scope (back from
 *   /billing) keeps the local cache, never the login-time profile, and
 *   writes nothing on arrival. Nothing pushes before hydration, and the
 *   interview waits on it.
 * - On later local changes (answer recorded, completion, checklist progress):
 *   push the whole snapshot up (deduped via JSON.stringify).
 *
 * Mount once inside the authenticated tree (AppLayout, next to the other sync
 * hooks).
 */
export function useOnboardingServerSync(): void {
  const configVersion = useOnboardingStore((s) => s.configVersion)
  const answers = useOnboardingStore((s) => s.answers)
  const persona = useOnboardingStore((s) => s.persona)
  const startedAt = useOnboardingStore((s) => s.startedAt)
  const completedAt = useOnboardingStore((s) => s.completedAt)
  const skipped = useOnboardingStore((s) => s.skipped)
  const checklist = useOnboardingStore((s) => s.checklist)
  const tipsSeen = useOnboardingStore((s) => s.tipsSeen)
  const activation = useOnboardingStore((s) => s.activation)
  const origin = useOnboardingStore((s) => s.origin)
  const hydrated = useOnboardingStore((s) => s.hydrated)
  const setScope = useOnboardingStore((s) => s.setScope)
  const hydrate = useOnboardingStore((s) => s.hydrate)
  const markHydrated = useOnboardingStore((s) => s.markHydrated)
  const scopeId = useAccountStore((s) => (s.session?.user ? storageScopeId(s.session.user) : null))
  const user = useAccountStore((s) => s.user)
  // Read-only view of AuthedApp's profile request (no subscription, no fetch).
  // Only mock auth gets here without a profile: the AuthGuard holds real
  // sessions until it loads.
  const { isError: profileFailed } = edgeUsersApi.endpoints.getUser.useQueryState(undefined, {
    selectFromResult: ({ isError }) => ({ isError }),
  })
  const [updateUser] = useUpdateUserMutation()
  const lastSynced = useRef<string | null>(null)
  // Hydrated for the door only, after a failed profile request: the scope's
  // server copy is still owed, so nothing pushes until a profile loads.
  const provisional = useRef(false)

  // Declared before the sync effect so a new scope is loaded before it runs.
  useEffect(() => {
    // A new scope starts a new dedupe baseline, seeded by the effect below.
    lastSynced.current = null
    provisional.current = false
    setScope(scopeId)
    return () => setScope(null)
  }, [scopeId, setScope])

  useEffect(() => {
    if (!scopeId) return
    // RTK keeps getUser cached past logout, so the account store can briefly
    // hold the previous account's profile. Not this scope's: treat it as
    // still loading, never hydrate or push from it.
    const profile = user && storageScopeId(user) === scopeId ? user : null

    // First profile load for this scope (or the one still owed after a
    // failed request): server wins. Read the store, not this render's
    // slices: the scope effect may have just replaced them. Return BEFORE
    // the push path: `hydrate` re-runs this effect with the server state.
    const store = useOnboardingStore.getState()
    if (!store.hydrated || provisional.current) {
      if (!profile) {
        // Failed request (mock auth only): open the door, but provisionally.
        if (profileFailed && !provisional.current) {
          provisional.current = true
          lastSynced.current = JSON.stringify(onboardingSnapshot(store))
          markHydrated({ provisional: true })
        }
        return // otherwise still loading: hold
      }
      const owed = provisional.current
      provisional.current = false
      const server = profile.onboarding != null ? validateOnboardingState(profile.onboarding) : null
      // Work done while the profile was unavailable is not discarded.
      if (server && hasProgress(server) && !(owed && hasProgress(store))) {
        lastSynced.current = JSON.stringify(server)
        hydrate(server)
        return
      }
      markHydrated()
      // No saved value → don't write on login; seed to local. When owed,
      // fall through and push what changed while provisional.
      if (!owed) {
        lastSynced.current = JSON.stringify(onboardingSnapshot(store))
        return
      }
    }

    // Scope already hydrated earlier this page lifetime: its local cache is
    // current. Seed the baseline instead of writing on arrival.
    if (lastSynced.current === null) {
      lastSynced.current = JSON.stringify(onboardingSnapshot(store))
      return
    }

    // Later local changes: push the whole snapshot up (deduped).
    const local = onboardingSnapshot({
      configVersion,
      answers,
      persona,
      startedAt,
      completedAt,
      skipped,
      checklist,
      tipsSeen,
      activation,
      origin,
    })
    const serialized = JSON.stringify(local)
    if (serialized !== lastSynced.current) {
      lastSynced.current = serialized
      void updateUser({ onboarding: local })
    }
  }, [
    scopeId,
    user,
    profileFailed,
    hydrated,
    configVersion,
    answers,
    persona,
    startedAt,
    completedAt,
    skipped,
    checklist,
    tipsSeen,
    activation,
    origin,
    hydrate,
    markHydrated,
    updateUser,
  ])
}
